// Shared helpers for the campervan fleet: slugs, lookup, and the aff-tagged
// DriveAway booking handoff. Used by the fleet table (page.tsx) and the
// per-vehicle detail page ([slug]/page.tsx).
import { withBase } from '../lib/brand';
import fleet from './vehicles.json';

export type Vehicle = {
  name: string;
  brand: string;
  class: string;
  berth: string;
  sleeps: string;
  transmission: string;
  shower_toilet: string;
  water_tank: string;
  awning: string;
  four_wd: boolean;
  image?: string;   // DriveAway partner-CDN clearcut photo
  da_url?: string;  // DriveAway model page (research + book)
};

export const FLEET = fleet as { updated: string; source: string; vehicles: Vehicle[] };

export const CLASS_LABEL: Record<string, string> = {
  small: 'Small · 2 berth',
  medium: 'Medium · 4–5 berth',
  large: 'Large · 6 berth',
};

/** A fleet spec is "present" unless it reads No / None / empty. */
export function hasSpec(value: string | boolean): boolean {
  if (typeof value === 'boolean') return value;
  const v = (value || '').trim().toLowerCase();
  return v !== '' && v !== 'no' && v !== 'none' && v !== 'n/a' && v !== '-';
}

/** Stable on-site slug from the (already brand-prefixed, unique) model name. */
export function vehicleSlug(v: Pick<Vehicle, 'name'>): string {
  return v.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function findVehicleBySlug(slug: string): Vehicle | undefined {
  return FLEET.vehicles.find((v) => vehicleSlug(v) === slug);
}

/** On-site detail page path for a vehicle (manual assetPrefix — no basePath). */
export function vehiclePath(v: Pick<Vehicle, 'name'>): string {
  return withBase(`/campervan-hire/${vehicleSlug(v)}`);
}

// DriveAway motorhome depot cities → book-my-ride slugs (the quotes engine
// wants the slug, not the name). Gold Coast isn't a motorhome depot, so it's
// intentionally excluded. Verified against /v1/cities.
export const DEPOT_CITY_SLUGS: Record<string, string> = {
  Sydney: 'syd',
  Melbourne: 'mel',
  Brisbane: 'bne',
  Cairns: 'cns',
  Adelaide: 'adl',
  Perth: 'per',
  Darwin: 'drw',
  Broome: 'bme',
  'Alice Springs': 'asp',
  Hobart: 'hba',
};

/**
 * Deep-link into DriveAway's LIVE motorhome engine for a city + dates, carrying
 * the CAMPAUS affiliate. (Their engine has no per-vehicle URL, so this lands on
 * the dated results list where the chosen van appears if it's available.)
 */
export function motorhomeQuotesUrl(o: {
  citySlug: string;
  pickupDate: string;
  dropoffDate: string;
  driverAge?: number;
  passengers?: number;
}): string {
  const p = new URLSearchParams({
    locale: 'en',
    agency: 'dah-au',
    conversion: 'CAMPAUS',
    campaign: '0',
    pickUpDate: o.pickupDate,
    dropOffDate: o.dropoffDate,
    driverAge: String(o.driverAge ?? 35),
    passengers: String(o.passengers ?? 2),
    pickUpCountry: 'au',
    pickUpCity: o.citySlug,
    currency: 'AUD',
  });
  return `https://motorhomes.driveaway.com.au/quotes?${p.toString()}`;
}

/**
 * Booking/research handoff: the van's own DriveAway model page, tagged with the
 * CAMPAUS affiliate so the commission is attributed (DriveAway has no instant
 * campervan booking — users research + book/enquire on that page). Falls back
 * to the fleet page if a vehicle somehow lacks a model URL.
 */
export function vehicleBookingUrl(v: Vehicle): string {
  const base = v.da_url || 'https://www.driveaway.com.au/motorhome-rentals-australia/';
  const sep = base.includes('?') ? '&' : '?';
  const params = new URLSearchParams({
    aff: 'CAMPAUS',
    utm_source: 'campingaustralia',
    utm_medium: 'referral',
    utm_content: 'campervan-detail',
  });
  return `${base}${sep}${params.toString()}`;
}

/** A short, honest one-liner about a van, built from its real specs. */
export function vehicleBlurb(v: Vehicle): string {
  const cls =
    v.class === 'small' ? 'compact campervan' : v.class === 'large' ? 'large motorhome' : 'mid-size camper';
  const bits: string[] = [`The ${v.name} is a ${cls} from ${v.brand}, sleeping ${v.sleeps}`];
  const extras: string[] = [];
  if (hasSpec(v.shower_toilet)) extras.push('an ensuite shower & toilet');
  if (hasSpec(v.awning)) extras.push('an awning');
  if (v.four_wd) extras.push('off-road 4WD capability');
  if (extras.length) {
    bits.push(
      extras.length === 1
        ? `with ${extras[0]}`
        : `with ${extras.slice(0, -1).join(', ')} and ${extras[extras.length - 1]}`,
    );
  }
  return `${bits.join(' ')}. ${v.transmission} transmission · ${v.water_tank} water · unlimited km.`;
}
